import { useEffect, useState, type FormEvent } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  apiClient,
  extractErrorMessage,
  saveTokens,
  type AuthTokens,
} from "@/lib/api-client";
import { useAuthStore, type Account } from "@/stores/auth-store";

type ConsentKey = "age" | "terms" | "privacy";

/** 가입 필수 동의 항목. 개인정보 수집·이용 동의는 약관과 따로 받는다. */
const CONSENTS: { key: ConsentKey; label: string; href?: string }[] = [
  { key: "age", label: "만 14세 이상입니다" },
  { key: "terms", label: "이용약관 동의", href: "/terms" },
  { key: "privacy", label: "개인정보 수집·이용 동의", href: "/privacy" },
];

export function SignupPage(): JSX.Element {
  const navigate = useNavigate();
  const location = useLocation();
  const setSession = useAuthStore((s) => s.setSession);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [consents, setConsents] = useState<Record<ConsentKey, boolean>>({
    age: false,
    terms: false,
    privacy: false,
  });
  const allAgreed = CONSENTS.every((c) => consents[c.key]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // 초대 링크에서 온 경우 이메일 prefill — 사용자가 다른 이메일로 가입 못 하게.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const prefillEmail = params.get("email");
    if (prefillEmail) setEmail(prefillEmail);
  }, [location.search]);

  const onSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setError(null);
    if (password.length < 8) {
      setError("비밀번호는 8자 이상이어야 합니다");
      return;
    }
    if (!allAgreed) {
      setError("필수 항목에 모두 동의해야 가입할 수 있습니다");
      return;
    }
    setSubmitting(true);
    try {
      const { data: tokens } = await apiClient.post<AuthTokens>("/auth/signup", {
        email,
        password,
        name: name.trim() || undefined,
        // 위에서 필수 동의 3개를 모두 확인한 뒤에만 여기까지 온다
        agreedToTerms: true,
      });
      saveTokens(tokens);
      const { data: account } = await apiClient.get<Account>("/auth/me");
      setSession(tokens, account);
      const params = new URLSearchParams(location.search);
      const invitation = params.get("invitation");
      if (invitation) {
        navigate(
          `/invitations/accept?token=${encodeURIComponent(invitation)}`,
          { replace: true },
        );
      } else {
        navigate("/app");
      }
    } catch (err) {
      setError(extractErrorMessage(err, "회원가입에 실패했습니다"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="container flex min-h-[calc(100vh-3.5rem)] items-center justify-center py-12">
      <div className="w-full max-w-sm">
        <div className="mb-8 text-center">
          <h1 className="text-2xl font-bold tracking-tight">시작해 볼까요</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            가입 즉시 데스크톱 앱을 받을 수 있어요. 무료로 시작합니다.
          </p>
        </div>
        <form onSubmit={onSubmit} className="space-y-4">
          <div>
            <label htmlFor="name" className="text-sm font-medium">
              이름 <span className="text-muted-foreground">(선택)</span>
            </label>
            <Input
              id="name"
              type="text"
              autoComplete="name"
              maxLength={100}
              className="mt-1.5"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          <div>
            <label htmlFor="email" className="text-sm font-medium">
              이메일
            </label>
            <Input
              id="email"
              type="email"
              autoComplete="email"
              required
              className="mt-1.5"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          <div>
            <label htmlFor="password" className="text-sm font-medium">
              비밀번호 <span className="text-muted-foreground">(8자 이상)</span>
            </label>
            <Input
              id="password"
              type="password"
              autoComplete="new-password"
              required
              minLength={8}
              maxLength={128}
              className="mt-1.5"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </div>
          <fieldset className="space-y-2 rounded-md border border-border p-3 text-sm">
            <legend className="sr-only">가입 동의</legend>
            <label className="flex items-center gap-2 font-medium">
              <input
                type="checkbox"
                className="h-4 w-4 accent-primary"
                checked={allAgreed}
                onChange={(e) => {
                  const checked = e.target.checked;
                  setConsents({ age: checked, terms: checked, privacy: checked });
                }}
              />
              모두 동의
            </label>
            <div className="space-y-2 border-t border-border pt-2">
              {CONSENTS.map((c) => (
                <div key={c.key} className="flex items-center justify-between gap-2">
                  <label className="flex items-center gap-2 text-muted-foreground">
                    <input
                      type="checkbox"
                      className="h-4 w-4 accent-primary"
                      checked={consents[c.key]}
                      onChange={(e) =>
                        setConsents((prev) => ({ ...prev, [c.key]: e.target.checked }))
                      }
                    />
                    <span>
                      <span className="text-foreground">[필수]</span> {c.label}
                    </span>
                  </label>
                  {c.href && (
                    <Link
                      to={c.href}
                      target="_blank"
                      rel="noopener"
                      className="shrink-0 text-xs text-muted-foreground underline underline-offset-4 hover:text-foreground"
                    >
                      보기
                    </Link>
                  )}
                </div>
              ))}
            </div>
          </fieldset>
          {error && (
            <p className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {error}
            </p>
          )}
          <Button type="submit" className="w-full" disabled={submitting}>
            {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
            가입하기
          </Button>
        </form>
        <p className="mt-6 text-center text-sm text-muted-foreground">
          이미 계정이 있으신가요?{" "}
          <Link to="/login" className="font-medium text-foreground underline-offset-4 hover:underline">
            로그인
          </Link>
        </p>
      </div>
    </main>
  );
}
